import { useState, useEffect, useCallback } from "react";
import { getAuth } from "firebase/auth";
import {
  doc,
  onSnapshot,
  setDoc,
  getDocs,
  collection,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import ModalConfig from "../components/ModalConfig";
import { getSubCollection } from "../utils/getSubCollection";

export function useNumeroConfig(configKey, options = {}) {
  const auth = getAuth();

  const { ownerUid, orgId, onManualNumeroChange, seriesOptions } = options;

  const ownerUidSafe =
    ownerUid && ownerUid !== "__LOADING__" && ownerUid !== "__loading__"
      ? ownerUid
      : null;

  const ownerId = ownerUidSafe || auth.currentUser?.uid || null;

  const [modo, setModo] = useState("auto");
  const [prefijo, setPrefijo] = useState("");
  const [siguiente, setSiguiente] = useState(1);
  const [padding, setPadding] = useState(4);

  const [serieId, setSerieId] = useState("serie_a");
  const [serieLabel, setSerieLabel] = useState("");
  const [reinicio, setReinicio] = useState("Anual");

  const [isOpen, setIsOpen] = useState(false);

  const getConfigRef = useCallback(() => {
    if (orgId) return doc(db, "orgs", orgId, "settings", configKey);

    if (!ownerId) return null;
    return doc(db, "usuarios", ownerId, "configuracion", configKey);
  }, [orgId, ownerId, configKey]);

  const getCotizacionesCollectionRef = useCallback(() => {
    if (orgId) return collection(db, "orgs", orgId, "cotizaciones");
    if (!ownerId) return null;
    return getSubCollection(ownerId, "cotizaciones");
  }, [orgId, ownerId]);

  useEffect(() => {
    if (!configKey) return;

    const ref = getConfigRef();
    if (!ref) return;

    const unsubscribe = onSnapshot(
      ref,
      async (snap) => {
        if (snap.exists()) {
          const data = snap.data() || {};

          const modoDb = data.modo || "auto";
          const prefDb = data.prefijo || "";
          const sigDb =
            Number.isFinite(Number(data.siguienteNumero)) &&
            Number(data.siguienteNumero) > 0
              ? Number(data.siguienteNumero)
              : 1;

          const padDb =
            Number.isFinite(Number(data.padding)) && Number(data.padding) > 0
              ? Number(data.padding)
              : 4;

          // ✅ Auto-reparación: si el doc existe pero el consecutivo está detrás
          let sigFinal = sigDb;

          try {
            if (modoDb === "auto") {
              const cotRef = getCotizacionesCollectionRef();
              if (cotRef) {
                const snapCot = await getDocs(cotRef);

                let max = 0;
                snapCot.forEach((d) => {
                  const raw = (d.data()?.numeroCotizacion ?? "").toString();
                  const n = parseInt(raw.replace(/\D/g, ""), 10);
                  if (Number.isFinite(n)) max = Math.max(max, n);
                });

                const shouldBe = max + 1;
                if (shouldBe > sigDb) {
                  sigFinal = shouldBe;

                  // 🔒 deja el doc consistente con lo real
                  await setDoc(
                    ref,
                    {
                      siguienteNumero: sigFinal,
                      ultimoNumeroUsado: Math.max(0, sigFinal - 1),
                    },
                    { merge: true },
                  );
                }
              }
            }
          } catch (e) {
            console.error("Auto-reparación de numeración falló:", e);
          }

          setModo(modoDb);
          setPrefijo(prefDb);
          setSiguiente(sigFinal);
          setPadding(padDb);

          setSerieId(data.serieId || "serie_a");
          setSerieLabel(data.serieLabel || "");
          setReinicio(data.reinicio || "Anual");

          return;
        }

        let siguienteNumero = 1;

        try {
          const cotRef = getCotizacionesCollectionRef();
          if (cotRef) {
            const snapCot = await getDocs(cotRef);
            let max = 0;

            snapCot.forEach((d) => {
              const raw = (d.data()?.numeroCotizacion ?? "").toString();
              const n = parseInt(raw.replace(/\D/g, ""), 10);
              if (Number.isFinite(n)) max = Math.max(max, n);
            });

            siguienteNumero = max + 1;
          }
        } catch (e) {
          console.error("Bootstrap getDocs cotizaciones falló:", e);
          siguienteNumero = 1;
        }

        try {
          await setDoc(
            ref,
            {
              modo: "auto",
              prefijo: "",
              siguienteNumero,
              padding: 4,
              serieId: "serie_a",
              serieLabel: "",
              reinicio: "Anual",
              ultimoNumeroUsado: Math.max(0, siguienteNumero - 1),
            },
            { merge: true },
          );
        } catch (e) {
          console.error("No se pudo crear config bootstrap:", e);
        }

        setModo("auto");
        setPrefijo("");
        setSiguiente(siguienteNumero);
        setPadding(4);
        setSerieId("serie_a");
        setSerieLabel("");
        setReinicio("Anual");
      },
      (error) => {
        console.error("onSnapshot settings error:", error);
      },
    );

    return unsubscribe;
  }, [configKey, getConfigRef, getCotizacionesCollectionRef]);

  const numero =
    modo === "auto"
      ? (() => {
          const p = (prefijo || "").trim();
          const n = String(siguiente ?? 1).padStart(padding || 1, "0");
          return p ? `${p}-${n}` : n;
        })()
      : "";

  const abrirConfiguracion = useCallback(() => setIsOpen(true), []);
  const cerrarConfiguracion = useCallback(() => setIsOpen(false), []);

  const handleOnChange = useCallback(
    async ({
      modo: newModo,
      prefijo: newPrefijo,
      siguiente: newSig,
      padding: newPad,
      serieId: newSerieId,
      serieLabel: newSerieLabel,
      reinicio: newReinicio,
      ultimoNumeroUsado,
      numeroManual,
    }) => {
      const ref = getConfigRef();
      if (!ref) return;

      const pad =
        Number.isFinite(Number(newPad)) && Number(newPad) > 0
          ? Number(newPad)
          : 4;

      const sig =
        Number.isFinite(Number(newSig)) && Number(newSig) > 0
          ? Number(newSig)
          : 1;

      const payload = {
        modo: newModo || "auto",
        prefijo: (newPrefijo || "").trim(),
        siguienteNumero: sig,
        padding: pad,
        ...(newSerieId ? { serieId: newSerieId } : {}),
        ...(newSerieLabel ? { serieLabel: newSerieLabel } : {}),
        ...(newReinicio ? { reinicio: newReinicio } : {}),
        ...(Number.isFinite(Number(ultimoNumeroUsado))
          ? { ultimoNumeroUsado: Number(ultimoNumeroUsado) }
          : { ultimoNumeroUsado: Math.max(0, sig - 1) }),
        ...(numeroManual ? { numeroManual: String(numeroManual) } : {}),
      };

      await setDoc(ref, payload, { merge: true });
      cerrarConfiguracion();
    },
    [getConfigRef, cerrarConfiguracion],
  );

  const NumeroConfigModalUI = useCallback(() => {
    if (!isOpen) return null;

    return (
      <ModalConfig
        isOpen={isOpen}
        onClose={cerrarConfiguracion}
        modoInicial={modo}
        prefijoInicial={prefijo}
        siguienteInicial={siguiente}
        paddingInicial={padding}
        serieIdInicial={serieId}
        serieLabelInicial={serieLabel}
        reinicioInicial={reinicio}
        seriesOptions={seriesOptions}
        onChangeNumeroFormatted={handleOnChange}
        onManualNumeroChange={onManualNumeroChange}
      />
    );
  }, [
    isOpen,
    cerrarConfiguracion,
    modo,
    prefijo,
    siguiente,
    padding,
    serieId,
    serieLabel,
    reinicio,
    seriesOptions,
    handleOnChange,
    onManualNumeroChange,
  ]);

  return {
    numero,
    modo,
    abrirConfiguracion,
    NumeroConfigModalUI,
  };
}
